// The repositories a group's workspace clones besides a GitHub source's own (S56): GitHub
// `owner/repo` with an optional branch, at most ten, each into its own directory named after
// the repository, so two may not share a name. The API checks the same; this says it early.
export const MAX_REPOSITORIES = 10;

export interface RepositoryRow {
  repo: string;
  branch: string;
}

export type RepositoryProblem = { kind: "tooMany" } | { kind: "repo" | "branch" | "duplicate"; row: number };

const NAME = /^[A-Za-z0-9_.-]+$/;

export function isRepo(text: string): boolean {
  const parts = text.split("/");
  return parts.length === 2 && parts.every((p) => NAME.test(p) && p !== "." && p !== "..");
}

export function isBranch(b: string): boolean {
  return (
    b.length >= 1 &&
    b.length <= 200 &&
    /^[A-Za-z0-9_./-]+$/.test(b) &&
    !/^[-/.]/.test(b) &&
    !/[/.]$/.test(b) &&
    !b.includes("..") &&
    !b.includes("//") &&
    !b.endsWith(".lock")
  );
}

/** The workspace directory a repository is cloned into (the API's `repo_dir`). */
export function repoDir(repo: string): string {
  const name = (repo.split("/").pop() ?? repo).toLowerCase();
  return name.replace(/[^a-z0-9]/g, "-").split("-").filter((p) => p !== "").join("-");
}

/** The first problem with the rows (blank rows are ignored), given the GitHub source's repo if any. */
export function repositoriesProblem(rows: RepositoryRow[], sourceRepo = ""): RepositoryProblem | null {
  const filled = rows.map((r, row) => ({ ...r, row })).filter((r) => r.repo.trim() !== "" || r.branch.trim() !== "");
  if (filled.length > MAX_REPOSITORIES) return { kind: "tooMany" };
  const taken = sourceRepo ? [repoDir(sourceRepo)] : [];
  for (const r of filled) {
    if (!isRepo(r.repo.trim())) return { kind: "repo", row: r.row };
    if (r.branch.trim() !== "" && !isBranch(r.branch.trim())) return { kind: "branch", row: r.row };
    const dir = repoDir(r.repo.trim());
    if (taken.includes(dir)) return { kind: "duplicate", row: r.row };
    taken.push(dir);
  }
  return null;
}

/** The API's `repositories`: filled rows, an empty branch left out. */
export function wireRepositories(rows: RepositoryRow[]): { repo: string; default_branch?: string }[] {
  return rows
    .filter((r) => r.repo.trim() !== "")
    .map((r) => (r.branch.trim() ? { repo: r.repo.trim(), default_branch: r.branch.trim() } : { repo: r.repo.trim() }));
}
